import type { ClipIR, Scene } from "./types";
import { songPatternOrder } from "./types";

/** Resolve a scene by id (falls back to first scene). */
export function findScene(clip: ClipIR, id: string | undefined): Scene | undefined {
  if (!clip.scenes?.length) {
    return undefined;
  }
  if (!id) {
    return clip.scenes[0];
  }
  return clip.scenes.find(scene => scene.id === id) ?? clip.scenes[0];
}

/** Next unused `S{n}` id. */
export function nextSceneId(clip: ClipIR): string {
  const used = new Set((clip.scenes ?? []).map(scene => scene.id));
  let n = clip.scenes?.length ?? 0;
  while (used.has(`S${n}`)) {
    n += 1;
  }
  return `S${n}`;
}

/**
 * Ensure `clip.scenes` exists and `clip.order` is scene ids.
 * Legacy clips stored pattern ids in `order` with no scenes — wrap into `S0`.
 */
export function normalizeClipScenes(clip: ClipIR): void {
  if (clip.scenes && clip.scenes.length > 0) {
    for (const scene of clip.scenes) {
      if (!Array.isArray(scene.order)) {
        scene.order = [];
      }
      scene.order = scene.order.filter(pid => clip.patterns.some(p => p.id === pid));
    }
    const validSceneIds = new Set(clip.scenes.map(scene => scene.id));
    const song = (clip.order ?? []).filter(id => validSceneIds.has(id));
    clip.order = song.length > 0 ? song : [clip.scenes[0]!.id];
    return;
  }

  const patternIds = (clip.order ?? []).filter(id => clip.patterns.some(p => p.id === id));
  const fallback =
    patternIds.length > 0 ? patternIds : clip.patterns.map(pattern => pattern.id);
  const scene: Scene = { id: "S0", order: [...fallback] };
  clip.scenes = [scene];
  clip.order = ["S0"];
}

/** Pattern ids in one scene (repeats preserved; missing patterns dropped). */
export function expandSceneOrder(clip: ClipIR, sceneId: string): string[] {
  normalizeClipScenes(clip);
  const scene = clip.scenes?.find(entry => entry.id === sceneId);
  if (!scene) {
    return [];
  }
  return scene.order.filter(pid => clip.patterns.some(p => p.id === pid));
}

/** Pattern ids for the full song (scenes expanded in order). */
export function expandSongOrder(clip: ClipIR): string[] {
  normalizeClipScenes(clip);
  return songPatternOrder(clip);
}

/** Remove a pattern id from every scene chain. */
export function removePatternFromScenes(clip: ClipIR, patternId: string): void {
  normalizeClipScenes(clip);
  for (const scene of clip.scenes ?? []) {
    scene.order = scene.order.filter(id => id !== patternId);
  }
}

/** Append a pattern ref to the given scene (or first scene). */
export function appendPatternToScene(
  clip: ClipIR,
  patternId: string,
  sceneId?: string,
): void {
  normalizeClipScenes(clip);
  const scene = findScene(clip, sceneId) ?? clip.scenes![0]!;
  scene.order.push(patternId);
}

/** Replace a scene's pattern chain (unknown pattern ids dropped). */
export function setSceneOrder(clip: ClipIR, sceneId: string, order: string[]): void {
  normalizeClipScenes(clip);
  const scene = findScene(clip, sceneId);
  if (!scene) {
    return;
  }
  const valid = new Set(clip.patterns.map(pattern => pattern.id));
  scene.order = order.filter(id => valid.has(id));
}

/**
 * Resolve a typed token to a pattern id: `P2` / `p2` / `2` / exact id / pattern name.
 */
export function resolvePatternToken(clip: ClipIR, raw: string): string | undefined {
  const token = raw.trim();
  if (!token) {
    return undefined;
  }
  const lower = token.toLowerCase();
  const byId = clip.patterns.find(pattern => pattern.id.toLowerCase() === lower);
  if (byId) {
    return byId.id;
  }

  const num = /^(?:p)?(\d+)$/i.exec(token);
  if (num) {
    const id = `P${Number(num[1])}`;
    if (clip.patterns.some(pattern => pattern.id === id)) {
      return id;
    }
    const idRaw = `P${num[1]}`;
    if (idRaw !== id && clip.patterns.some(pattern => pattern.id === idRaw)) {
      return idRaw;
    }
  }

  return clip.patterns.find(pattern => pattern.name?.trim().toLowerCase() === lower)?.id;
}

/** Split whitespace/comma text into pattern ids; unknown tokens listed separately. */
export function parsePatternTokens(
  clip: ClipIR,
  text: string,
): { ids: string[]; unknown: string[] } {
  const parts = text.split(/[\s,]+/).filter(Boolean);
  const ids: string[] = [];
  const unknown: string[] = [];
  for (const part of parts) {
    const id = resolvePatternToken(clip, part);
    if (id) {
      ids.push(id);
    } else {
      unknown.push(part);
    }
  }
  return { ids, unknown };
}

/** Apply arrangement JSON (from lab/project persistence) onto a parsed clip. */
export function applyClipArrangement(
  clip: ClipIR,
  arrangement: { scenes?: Scene[]; order?: string[] } | undefined,
): void {
  if (arrangement?.scenes && arrangement.scenes.length > 0) {
    clip.scenes = arrangement.scenes.map(scene => ({
      id: scene.id,
      ...(scene.name ? { name: scene.name } : {}),
      order: Array.isArray(scene.order) ? [...scene.order] : [],
    }));
    if (arrangement.order && arrangement.order.length > 0) {
      clip.order = [...arrangement.order];
    } else {
      clip.order = [clip.scenes[0]!.id];
    }
  }
  normalizeClipScenes(clip);
}

/** Snapshot of scenes + song order for persistence alongside DSL. */
export function clipArrangementSnapshot(clip: ClipIR): {
  scenes: Scene[];
  order: string[];
} {
  normalizeClipScenes(clip);
  return {
    scenes: (clip.scenes ?? []).map(scene => ({
      id: scene.id,
      ...(scene.name ? { name: scene.name } : {}),
      order: [...scene.order],
    })),
    order: [...clip.order],
  };
}
